/**
 * Saved views (registry views.saved-views, .create, .save-from-filters, .edit-duplicate-delete, .visibility, .owner;
 * architecture §2.7 "views", §3.11 `custom_view.filter_data` LWW): the rules both sides share.
 *
 * - URLs: `/<ws>/view/<ascii-name>-<slugId>`; the slug id is the last 12 hex digits of the view's client-minted
 *   UUIDv7, so the saving client knows the URL before the server answers and a rename never breaks a link (the 12-hex
 *   id resolves the view, the name part is decorative and replaced, saved-views AC3).
 * - `filter_data` is a `@quadratic/views` filter AST (contracts can't import views): the rules here check the
 *   semantics per model (issue views use views-filters' `ISSUE_FIELD_RULES`; project views the table below). An empty
 *   object (`{}`) and `{ and: [] }` both mean "no conditions".
 * - Visibility: personal (`shared = false`, owner only), workspace (`shared`, no team), team (`shared` + `teamId`).
 *   A personal view tied to a team stays personal (2025-02-13 fix): `teamId` is then only the scope.
 */
import { asciiSlug, slugIdFromParam, slugIdOf } from '../../projects/core/slug';
import { cleanFilter, ISSUE_FIELD_RULES, MAX_FILTER_TEXT, type FieldRule } from '../filters/rules';

export { slugIdFromParam, slugIdOf };

export const VIEW_NAME_MAX = 120;
export const VIEW_DESCRIPTION_MAX = 2000;

/** User-facing copy shared by the server's refusals and the client's inline errors. */
export const VIEW_MESSAGES = {
  nameRequired: 'Name is required',
  nameTooLong: `Names can be at most ${VIEW_NAME_MAX} characters`,
  filtersInvalid: 'This view’s filters are no longer valid',
  guestShared: 'Guests can only save personal views',
  modelUnavailable: 'Initiative views aren’t available yet',
  ownerIneligible: 'Owner must be an active member who can see this view',
  saveFailed: 'Couldn’t save the view',
  deleteFailed: 'Couldn’t delete the view',
  transferFailed: 'Couldn’t transfer ownership',
} as const;

// ---------------------------------------------------------------------------------------------------------------
// URLs

/** The `$slug` route param of a view ('open-bugs-a1b2c3d4e5f6'; the bare slug id when the name has no ASCII). */
export function viewSlug(name: string, slugId: string): string {
  const s = asciiSlug(name);
  return s ? `${s}-${slugId}` : slugId;
}

/** Workspace-relative path of a view. */
export function viewPath(name: string, slugId: string): `/${string}` {
  return `/view/${viewSlug(name, slugId)}`;
}

export const VIEW_KINDS = ['issues', 'projects'] as const;
export type ViewKind = (typeof VIEW_KINDS)[number];

export function isViewKind(v: unknown): v is ViewKind {
  return typeof v === 'string' && (VIEW_KINDS as readonly string[]).includes(v);
}

export function kindOfModel(model: unknown): ViewKind {
  return model === 'project' ? 'projects' : 'issues';
}

export function modelOfKind(kind: ViewKind): 'issue' | 'project' {
  return kind === 'projects' ? 'project' : 'issue';
}

// ---------------------------------------------------------------------------------------------------------------
// visibility

export type Visibility = 'personal' | 'workspace' | 'team';

/** The visibility of a stored or draft view (a personal view keeps its team as a scope only). */
export function visibilityOf(v: { readonly shared?: unknown; readonly teamId?: unknown }): Visibility {
  if (v.shared !== true) return 'personal';
  return typeof v.teamId === 'string' ? 'team' : 'workspace';
}

// ---------------------------------------------------------------------------------------------------------------
// filter data

export const PROJECT_STATUS_TYPES = ['backlog', 'planned', 'started', 'paused', 'completed', 'canceled'] as const;
export const PROJECT_HEALTH = ['onTrack', 'atRisk', 'offTrack'] as const;

/** Project filter fields (the ones `@quadratic/views`' project adapter evaluates). */
export const PROJECT_FIELD_RULES: Readonly<Record<string, FieldRule>> = {
  status: { family: 'is', value: { kind: 'id', nullable: true } },
  statusType: { family: 'is', value: { kind: 'enum', values: PROJECT_STATUS_TYPES } },
  health: { family: 'is', value: { kind: 'enum', values: PROJECT_HEALTH, nullable: true } },
  lead: { family: 'is', value: { kind: 'id', tokens: ['me'], nullable: true } },
  creator: { family: 'is', value: { kind: 'id', tokens: ['me'], nullable: true } },
  leadTeam: { family: 'is', value: { kind: 'id', nullable: true } },
  team: { family: 'includes', value: { kind: 'id', tokens: ['myTeams'], nullable: true } },
  labels: { family: 'includes', value: { kind: 'id', nullable: true } },
  member: { family: 'isMulti', value: { kind: 'id', tokens: ['me'] } },
  milestone: { family: 'includes', value: { kind: 'id', tokens: ['no-work'] } },
  priority: { family: 'is', value: { kind: 'enum', values: [0, 1, 2, 3, 4] } },
  startDate: { family: 'date', value: { kind: 'date' } },
  targetDate: { family: 'date', value: { kind: 'date' } },
  createdAt: { family: 'date', value: { kind: 'date' } },
  updatedAt: { family: 'date', value: { kind: 'date' } },
  completedAt: { family: 'date', value: { kind: 'date' } },
  title: { family: 'text', value: { kind: 'text' } },
};

export function fieldRulesOf(model: unknown): Readonly<Record<string, FieldRule>> | null {
  if (model === 'issue') return ISSUE_FIELD_RULES;
  if (model === 'project') return PROJECT_FIELD_RULES;
  return null;
}

/** Whether a stored filter holds no condition (`{}`, `{ and: [] }`, nested empty groups). */
export function isEmptyFilterData(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return true;
  const o = raw as Record<string, unknown>;
  if (Object.keys(o).length === 0) return true;
  for (const k of ['and', 'or'] as const) {
    if (k in o && Array.isArray(o[k])) return (o[k] as unknown[]).every(isEmptyFilterData);
  }
  return false;
}

/**
 * Why a view's `filter_data` can't be saved, or null: every condition must be applicable to the view's model (the
 * same check a URL filter gets, views.filters.operators AC9), groups nest at most three levels, and no text value
 * passes `MAX_FILTER_TEXT`. `{}` is the empty filter.
 */
export function filterDataProblem(model: unknown, raw: unknown): string | null {
  const rules = fieldRulesOf(model);
  if (!rules) return 'unsupported view model';
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return 'filter_data is not an object';
  if (Object.keys(raw).length === 0) return null;
  const { dropped } = cleanFilter(raw, rules);
  if (dropped > 0) return `${dropped} condition${dropped === 1 ? '' : 's'} can't be applied`;
  return JSON.stringify(raw).length > 64 * 1024 ? 'filter_data too large' : null;
}

/** The fields whose values are entity ids, by model, and the model each id names. */
export const ID_FIELDS: Readonly<Record<'issue' | 'project', Readonly<Record<string, string>>>> = {
  issue: {
    status: 'WorkflowState',
    assignee: 'User',
    creator: 'User',
    subscriber: 'User',
    project: 'Project',
    projectStatus: 'ProjectStatus',
    milestone: 'ProjectMilestone',
    cycle: 'Cycle',
    team: 'Team',
    parent: 'Issue',
    labels: 'IssueLabel',
  },
  project: {
    status: 'ProjectStatus',
    lead: 'User',
    creator: 'User',
    member: 'User',
    leadTeam: 'Team',
    team: 'Team',
  },
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Every entity id a filter names, as `model → ids` (tokens such as `me` and `null` are skipped). */
export function filterRefs(model: unknown, raw: unknown): Map<string, Set<string>> {
  const fields = model === 'issue' || model === 'project' ? ID_FIELDS[model] : {};
  const out = new Map<string, Set<string>>();
  const visit = (node: unknown, depth: number): void => {
    if (!node || typeof node !== 'object' || Array.isArray(node) || depth > 6) return;
    const o = node as Record<string, unknown>;
    for (const k of ['and', 'or'] as const) {
      if (Array.isArray(o[k])) {
        for (const kid of o[k] as unknown[]) visit(kid, depth + 1);
        return;
      }
    }
    const target = typeof o.field === 'string' ? fields[o.field] : undefined;
    if (!target) return;
    const values = Array.isArray(o.value) ? (o.value as unknown[]) : [o.value];
    for (const v of values) {
      if (typeof v !== 'string' || !UUID.test(v)) continue;
      const set = out.get(target) ?? new Set<string>();
      set.add(v.toLowerCase());
      out.set(target, set);
    }
  };
  visit(raw, 0);
  return out;
}

/** The ids `next` names that `prev` didn't (only those must resolve when a filter is saved; hidden values stay). */
export function addedFilterRefs(model: unknown, next: unknown, prev: unknown): Map<string, Set<string>> {
  const before = filterRefs(model, prev);
  const out = new Map<string, Set<string>>();
  for (const [m, ids] of filterRefs(model, next)) {
    const had = before.get(m);
    const added = new Set([...ids].filter((id) => !had?.has(id)));
    if (added.size) out.set(m, added);
  }
  return out;
}

export { MAX_FILTER_TEXT };

// ---------------------------------------------------------------------------------------------------------------
// names

/** A view name as saved: trimmed; '' when blank. */
export function cleanName(name: unknown): string {
  return typeof name === 'string' ? name.trim() : '';
}

/** The default name of a duplicate ('Open bugs (copy)'), cut to the name limit. */
export function copyName(name: string): string {
  const suffix = ' (copy)';
  const base = name.length + suffix.length > VIEW_NAME_MAX ? name.slice(0, VIEW_NAME_MAX - suffix.length).trimEnd() : name;
  return `${base}${suffix}`;
}
